import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { StoreContextService } from '../services/store-context.service';
import { StorageKey, StorageService } from '../services/storage.service';
import { HubDeviceService } from '../offline/hub-device.service';
import { HubOriginService } from '../offline/hub-origin.service';
import { HubRoutingService } from '../offline/hub-routing.service';
import { NON_POS_SURFACE } from '../offline/hub-surfaces';

/**
 * Offline Multi-Mode — base URL rewriter.
 *
 * When a store has offline multi-mode configured (offlineEnabled=true +
 * offlineHubUrl set) and the network is offline, rewrites all outbound
 * /api/* calls so only the host changes — the full /api/... path is
 * preserved — routing the request to the Store Edge Agent instead of
 * the cloud. No Angular component or service needs modification.
 *
 * e.g.  https://api.example.com/staging/api/pos/orders
 *   →   http://192.168.1.10:8423/api/pos/orders
 *
 * Matching is done by locating the first '/api/' segment in the URL
 * (mirrors HubRoutingService.rewriteForHub), so it is independent of
 * environment.apiUrl and works correctly even when ApiService caches
 * the base URL at construction time.
 *
 * LH-4 (docs/LOCAL-HUB-LOOPBACK.md) — when the POS is running ON the hub
 * device itself, the LAN `{hash}` URL is the wrong target (it resolves to a
 * stale or self LAN IP and fails at the TCP layer). Instead we dial the
 * loopback URL (https://local.hub.rms.ipossoft.io:9799 → 127.0.0.1), which
 * always reaches the local hub server on the same machine — even when the
 * box has no LAN at all and was never provisioned a `{hash}` record.
 *
 * Must be registered as the FIRST interceptor so that auth and offline
 * interceptors operate on the already-rewritten URL.
 */
/**
 * OF-FIX-C7 (2026-08-01) — surfaces with no offline implementation.
 *
 * This interceptor is registered app-wide, but only the POS surface has
 * a hub mirror behind it. Kiosk, KDS, pickup-display and the customer
 * display have no offline story at all, yet their `/api/*` calls were
 * still being rewritten to a hub that has no handlers for them.
 *
 * B2 defused the dangerous half of that (unmirrored routes now fail
 * closed instead of returning a fake 200). This closes the structural
 * half: those surfaces should talk to the cloud and fail honestly like
 * any other online-only client, rather than being silently redirected to
 * a box that cannot serve them.
 *
 * It matters most on the kiosk, which is unattended and takes payment —
 * routing it at a hub that can only 503 buys nothing and adds a failure
 * mode with no staff present to interpret it.
 *
 * Deliberately a DENY-list of known non-POS surfaces rather than an
 * allow-list of POS routes: an allow-list that missed a POS route would
 * silently disable offline for it, whereas a surface missing from this
 * list simply keeps today's behaviour. Matches `modeToUrl()` in
 * core/utils/mode-routing.ts — keep the two in step. The list lives in
 * core/offline/hub-surfaces.ts (NON_POS_SURFACE); since OFP-082 it also
 * holds the "unavailable" placeholders and the Payment Terminal's routes,
 * and a device marked as a Payment Terminal is never rewritten at all
 * (PTM D7: online only — its cold boot passes through `/pos/s/<code>`).
 */
/**
 * POSTs that are safe to replay against the hub after a transport-level
 * failure, even without an Idempotency-Key.
 *
 * `pin-login` verifies a PIN and returns a token — it records nothing, so
 * a retry cannot double-apply anything. It is also the call that must NOT
 * have a dead window: without it the cashier cannot get into the POS at
 * all, which is what "Connection error. Please try again." on the login
 * screen actually was when the WiFi went off.
 *
 * Keep this list tiny and read-only-in-effect. Anything that moves money
 * or creates a record is never retried here, keyed or not (OFP-010): a
 * status-0 failure does not prove the write missed, and a key does not
 * dedupe across the cloud and the hub.
 */
const SAFE_RETRY_POSTS = ['/auth/pin-login'];

export const hubBaseUrlInterceptor: HttpInterceptorFn = (req, next) => {
  if (typeof window === 'undefined') return next(req);
  // Caller inbox and consumption exist only in the cloud, never on the LAN hub.
  if (/\/api\/pos\/caller-id(?:\/|$|\?)/.test(req.url)) return next(req);

  const store = inject(StoreContextService);
  const storage = inject(StorageService);
  const hubDevice = inject(HubDeviceService);
  const hubOrigin = inject(HubOriginService);
  const hubRouting = inject(HubRoutingService);
  const router = inject(Router);

  const toBase = (r: HttpRequest<unknown>, base: string): HttpRequest<unknown> | null => {
    const clean = base.replace(/\/+$/, '');
    if (r.url.startsWith(clean)) return null;
    const idx = r.url.indexOf('/api/');
    if (idx < 0) return null;
    return r.clone({ url: `${clean}${r.url.substring(idx)}` });
  };

  // OFP-082 review — a device marked as a Payment Terminal never uses the
  // hub (PTM D7), on any route: it cold-boots through `/pos/s/<code>`, and
  // the marker is the cloud's word, cleared only by re-registration.
  const paytermDevice = !!(storage.getRaw(StorageKey.PAYTERM_DEVICE) ?? '').trim();
  const isPosSurface =
    !paytermDevice &&
    !NON_POS_SURFACE.test(router.url ?? '') &&
    !NON_POS_SURFACE.test(window.location.pathname ?? '');

  // RECON-13 (2026-08-23) — ask the ROUTER, not the raw network state.
  //
  // This line was `if (net.current() !== 'offline')`, and it is the only
  // thing that decides where a request actually goes. So
  // `HubRoutingService.decide()` — the whole point of which is to make
  // this decision — was never consulted by the request path at all. It
  // drove the banner and nothing else.
  //
  // Everything built on top of it was therefore display-only:
  //
  //   • RECON-4's drain gate did not really hold traffic on the hub. It
  //     only appeared to, because the 60s recovery hysteresis usually
  //     kept `net` 'offline' long enough for the drain to finish.
  //   • RECON-9's Manual hold never held anything. The banner offered
  //     "Go online" while every request had already been going to the
  //     cloud for however long `net` had said online.
  //   • RECON-10 then removed the accident: declaring recovery as soon as
  //     the outbox drained flipped `net` online immediately, so the
  //     terminal switched itself the moment the upload finished —
  //     reported as "before clicking Go online, it automatically
  //     switched to online". Exactly right, and this is why.
  //
  // `decide()` returns 'cloud' precisely when `net` is online AND no hold
  // applies, so this is behaviour-preserving apart from the holds it now
  // actually honours.
  // OF-SWITCH (2026-10-08) — ReconnectMode ConfirmBothWays, not yet
  // confirmed offline: requests stay on the cloud (and fail while the
  // internet is down) — no automatic retry against the hub. The one
  // exception is the confirm flow itself while its dialog is open: the
  // supervisor approval and the switch record must reach the hub, because
  // the cloud is exactly what is unreachable.
  if (hubRouting.confirmMode?.() === true && !hubRouting.isConfirmedOffline()) {
    const switchCall = /\/api\/pos\/(approvals\/authorize|offline-switch)(\?|$)/.test(req.url);
    if (switchCall && hubRouting.isSwitchFlowActive() && isPosSurface) {
      const hubBase = hubDevice.isHubDevice()
        ? (hubOrigin.isHubOrigin()
            ? window.location.origin
            : (hubDevice.loopbackHubUrl() ?? hubDevice.localHubUrl()))
        : (store.activeStore?.offlineEnabled === true
            ? (store.activeStore?.offlineHubUrl?.trim() || null)
            : null);
      const rewritten = hubBase ? toBase(req, hubBase) : null;
      if (rewritten) return next(rewritten);
    }
    return next(req);
  }

  if (hubRouting.decide() === 'cloud') {
    // OF-FIX-LB-8 — fast failover on the hub device.
    //
    // Everything below is gated on the heartbeat having already flipped
    // state to 'offline'. That probe runs every 10s, so when the network
    // drops mid-shift there is a window of up to 10s + the probe timeout
    // in which every API call still goes to the cloud and simply fails —
    // on a device that is itself running a healthy hub. On a single-device
    // store that reads as "the POS cannot talk to the edge client".
    //
    // So when THIS device is the hub, do not wait for the global flip:
    // if a request dies at the transport level, immediately retry it
    // against the loopback hub.
    //
    // Deliberately narrow:
    //   • status 0 only — no response was received. An HTTP error means
    //     the cloud DID answer, and the hub is not a substitute for a 4xx.
    //   • GET/HEAD, and pin-login. A blind retry of a POST is unsafe:
    //     status 0 does not prove the write never landed. Reads are the
    //     visible symptom (menu, checks, tables failing to load); writes
    //     keep the existing behaviour and are covered once state flips.
    //   • OFP-010 — NOT a keyed write either. An Idempotency-Key dedupes on
    //     ONE server: if the cloud applied the write and only the response
    //     was lost, the hub applies it again, and the hub's replay of it
    //     later fails at the cloud (the MVC filter and the drain dispatcher
    //     fingerprint the same key differently, a terminal 422).
    //
    // OF-HUB-DNS (2026-10-06) — a CLIENT terminal gets the same one retry,
    // against the store's LAN hub. Cold-started with the internet down it
    // sat in exactly that window (the first probe had not failed yet), so a
    // PIN typed straight away died with "Connection error" and was never
    // tried on the hub at all.
    const lanHub = store.activeStore?.offlineEnabled === true
      ? (store.activeStore?.offlineHubUrl?.trim() || null)
      : null;
    const local = hubDevice.isHubDevice()
      ? (hubDevice.loopbackHubUrl() ?? hubDevice.localHubUrl())
      : lanHub;
    const retryable =
      req.method === 'GET' ||
      req.method === 'HEAD' ||
      SAFE_RETRY_POSTS.some((p) => req.url.includes(p));
    if (!local || !retryable || !isPosSurface) return next(req);

    return next(req).pipe(
      catchError((err: unknown) => {
        if (!(err instanceof HttpErrorResponse) || err.status !== 0) {
          return throwError(() => err);
        }
        const rewritten = toBase(req, local);
        if (!rewritten) return throwError(() => err);
        // The global state catches up through error.interceptor, which sits
        // downstream of this one and has already reported this status 0
        // (OFP-082). A second, immediate probe here would count as a second
        // failure and undo the spacing that keeps a short blip from
        // flipping the terminal offline.
        return next(rewritten);
      }),
    );
  }

  // Router.url is base-href-relative and correct once navigation has
  // settled; location.pathname covers the pre-bootstrap window and any
  // deployment sub-path. Either matching is enough to opt out — and so is
  // the Payment Terminal device marker (isPosSurface folds all three).
  if (!isPosSurface) {
    return next(req);
  }

  // Pick the base URL to route /api/* to while offline:
  //   • This device IS the hub → loopback URL. Works regardless of LAN /
  //     provisioning state, so it stands alone (no offlineEnabled / hubUrl
  //     gate — isHubDevice already implies the cloud enabled offline).
  //   • Otherwise → the LAN {hash} URL, gated on the store having offline
  //     enabled and a provisioned hub URL (unchanged behaviour).
  let base: string | null = null;
  if (hubOrigin.isHubOrigin()) {
    // OF-FIX-LB-5 — the hub is already serving this page, so talk to it
    // on the origin we were loaded from.
    //
    // Without this the branch below would rewrite to
    // `https://local.hub.rms.ipossoft.io:9799` even on a page served
    // from `http://127.0.0.1:8080` — putting a public DNS lookup back in
    // front of every API call, on exactly the device that cannot perform
    // one. That is the failure the whole LB series exists to remove, and
    // it would have been invisible until the next store outage.
    //
    // Same-origin also means no CORS preflight (the logs showed one per
    // menu category) and no mixed-content question.
    base = window.location.origin;
  } else if (hubDevice.isHubDevice()) {
    base = (hubDevice.loopbackHubUrl() ?? hubDevice.localHubUrl());
  } else if (store.activeStore?.offlineEnabled === true) {
    base = store.activeStore?.offlineHubUrl?.trim() ?? null;
  }
  base = base ? base.replace(/\/+$/, '') : null;
  if (!base) return next(req);

  // Already pointing at the chosen base — nothing to do.
  if (req.url.startsWith(base)) return next(req);

  // Locate the /api/ boundary in the URL — same technique used by
  // HubRoutingService.rewriteForHub so behaviour is consistent.
  const marker = '/api/';
  const idx = req.url.indexOf(marker);
  if (idx < 0) return next(req); // Not an /api/ call, pass through.

  // Keep from '/api/' onwards: e.g. '/api/pos/orders'
  const apiPath = req.url.substring(idx);
  return next(req.clone({ url: `${base}${apiPath}` }));
};
